import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import client from "@/lib/api";
import {
  Buildings,
  Users,
  ClipboardText,
  CheckCircle,
  Clock,
  CalendarBlank,
  ArrowUpRight,
} from "@phosphor-icons/react";

const Stat = ({ icon: Icon, label, value, accent, testId }) => (
  <div className="trent-card p-5" data-testid={testId}>
    <div className="flex items-center justify-between mb-3">
      <div className={`w-9 h-9 flex items-center justify-center rounded-sm ${accent}`}>
        <Icon size={18} weight="duotone" />
      </div>
      <span className="text-[10px] uppercase tracking-[0.2em] text-[#6B7280]">{label}</span>
    </div>
    <div className="trent-heading text-3xl font-black tracking-tighter text-[#1E3A5F]">{value}</div>
  </div>
);

export default function Dashboard() {
  const [stats, setStats] = useState(null);

  const loadStats = useCallback(async () => {
    try {
      const r = await client.get("/dashboard/stats");
      setStats(r.data);
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  return (
    <div data-testid="dashboard-page">
      <div className="flex items-end justify-between mb-8">
        <div>
          <div className="text-[11px] uppercase tracking-[0.3em] text-[#6B7280] mb-1">
            Overview
          </div>
          <h1 className="trent-heading text-3xl md:text-4xl font-black tracking-tighter text-[#1E3A5F]">
            Inspection control room
          </h1>
        </div>
        <Link to="/admin-dashboard/inspections/new" className="trent-btn-primary hidden md:inline-flex" data-testid="dashboard-new-inspection">
          New inspection <ArrowUpRight weight="bold" size={16} />
        </Link>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        <Stat icon={Buildings} label="Properties" value={stats?.properties ?? "—"} accent="bg-[#E0F2FE] text-[#0369A1]" testId="stat-properties" />
        <Stat icon={Users} label="Landlords" value={stats?.landlords ?? "—"} accent="bg-[#FEF3C7] text-[#92400E]" testId="stat-landlords" />
        <Stat icon={ClipboardText} label="Inspections" value={stats?.inspections ?? "—"} accent="bg-[#DCFCE7] text-[#14532D]" testId="stat-inspections" />
        <Stat icon={Clock} label="Pending" value={stats?.pending ?? "—"} accent="bg-[#FEE2E2] text-[#991B1B]" testId="stat-pending" />
        <Stat icon={CheckCircle} label="Completed" value={stats?.completed ?? "—"} accent="bg-[#D1FAE5] text-[#065F46]" testId="stat-completed" />
      </div>

      <div className="mt-10 grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 trent-card p-6" data-testid="upcoming-card">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-[#6B7280]">Upcoming</div>
              <h2 className="trent-heading text-xl font-bold text-[#1E3A5F]">Scheduled inspections</h2>
            </div>
            <Link to="/admin-dashboard/inspections" className="text-xs font-semibold uppercase tracking-wider text-[#1E3A5F] underline">
              View all
            </Link>
          </div>
          {stats?.upcoming?.length ? (
            <div className="divide-y divide-[#E5E7EB]">
              {stats.upcoming.map((u) => (
                <Link
                  key={u.id}
                  to={`/admin-dashboard/inspections/${u.id}`}
                  className="flex items-center justify-between py-3 hover:bg-[#FAFAF7] -mx-2 px-2 transition-colors"
                  data-testid={`upcoming-row-${u.id}`}
                >
                  <div>
                    <div className="font-semibold text-[#1E3A5F]">{u.property_address || "—"}</div>
                    <div className="text-xs text-[#6B7280] mt-0.5">
                      {u.inspection_type} · {u.inspection_date} · {u.inspector_name}
                    </div>
                  </div>
                  <span className="condition-pill bg-[#FEF3C7] text-[#92400E] border-[#FBBF24]">
                    {u.status}
                  </span>
                </Link>
              ))}
            </div>
          ) : (
            <EmptyState />
          )}
        </div>

        <div className="bg-[#1E3A5F] text-white rounded-md p-6 relative overflow-hidden">
          <div className="trent-grid-bg absolute inset-0 opacity-15" />
          <div className="relative">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-[#16A34A] mb-2">
              <CalendarBlank size={14} /> Quickstart
            </div>
            <h3 className="trent-heading text-2xl font-black tracking-tight mb-3">
              Start an inspection in under 60 seconds.
            </h3>
            <p className="text-sm text-white/70 mb-6">
              Walk the property, capture photos, score conditions and export a tribunal-ready PDF.
            </p>
            <Link to="/admin-dashboard/inspections/new" className="trent-btn-accent" data-testid="quickstart-cta">
              Begin new inspection <ArrowUpRight weight="bold" size={14} />
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

const EmptyState = () => (
  <div className="text-center py-10 border border-dashed border-[#E5E7EB] rounded-md">
    <div className="text-sm text-[#6B7280]">No upcoming inspections yet.</div>
    <Link to="/admin-dashboard/inspections/new" className="trent-btn-secondary mt-3 inline-flex">
      Create one
    </Link>
  </div>
);
